import { FC, ReactElement, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { DialogMenu, SelectBox } from "@/components";
import { DUTY_ATTRIBUTES, filter_black_icon } from "@/constants";

export const initialFilters = {
  dutyType: "" as Attribute,
  importerNo: "",
  classification: "",
  entryNo: "",
  flowType: "",
  originCountry: "",
  exportCountry: "",
};

export type Filters = typeof initialFilters;

interface IAdvanceFilter {
  appliedFilters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onClose: () => void;
  anchorEl: Element | null;
}

const AdvanceFilter: FC<IAdvanceFilter> = ({
  appliedFilters,
  onFiltersChange,
  onClose,
  anchorEl,
}): ReactElement => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [areFilterApplying, setAreFilterApplying] = useState<boolean>(false);

  useEffect(() => {
    setFilters({ ...appliedFilters });
  }, [appliedFilters]);

  const applyFilters = async (): Promise<void> => {
    setAreFilterApplying(true);
    await onFiltersChange(filters);
    setAreFilterApplying(false);
    onClose();
  };

  const resetFilters = (): void => {
    setFilters(initialFilters);
  };

  const handleChangeValue =
    (name: keyof Filters) =>
    (value: string): void => {
      setFilters({
        ...filters,
        [name]: value,
      });
    };

  return (
    <DialogMenu
      id="advance-filter-menu"
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={filter_black_icon} alt="filter" />
          <Typography
            variant="h5"
            fontSize={18}
            fontWeight={600}
            color="#030712"
          >
            Advance Filter
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeContent: "center",
              backgroundColor: "error.main",
              color: "white",
              borderRadius: "50%",
            }}
          >
            <Typography variant="caption" fontWeight={500} color="white">
              {Object.values(filters).filter(Boolean).length}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            color: "#116B6F",
            textDecorationLine: "underline",
            cursor: "pointer",
          }}
          variant="body2"
          fontWeight={500}
          onClick={resetFilters}
        >
          Clear All
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={1}>
        <SelectBox
          label="Duty Paid"
          placeholder="Select Duty Type"
          value={filters.dutyType}
          handleChangeValue={handleChangeValue("dutyType")}
          options={DUTY_ATTRIBUTES}
        />
        <SelectBox
          label="Importer No."
          placeholder="Select Importer No."
          value={filters.importerNo}
          handleChangeValue={handleChangeValue("importerNo")}
          options={[
            { label: "123 Importer Number", value: "123 Importer Number" },
            { label: "456 Importer Number", value: "456 Importer Number" },
          ]}
        />
        <SelectBox
          label="Classification"
          placeholder="Select Classification"
          value={filters.classification}
          handleChangeValue={handleChangeValue("classification")}
          options={[
            { label: "6,092,982.00", value: "6,092,982.00" },
            { label: "9,592,786.00", value: "9,592,786.00" },
          ]}
        />
        <SelectBox
          label="Entry No."
          placeholder="Select Entry No."
          value={filters.entryNo}
          handleChangeValue={handleChangeValue("entryNo")}
          options={[
            { label: "971232", value: "971232" },
            { label: "7538294", value: "7538294" },
          ]}
        />
        <SelectBox
          label="Trade Flow"
          placeholder="Select Flow Type"
          value={filters.flowType}
          handleChangeValue={handleChangeValue("flowType")}
          options={[
            { label: "AE-CN-US", value: "AE-CN-US" },
            { label: "AE-CN-UK", value: "AE-CN-UK" },
          ]}
        />
        <SelectBox
          label="Country of Origin"
          placeholder="Select Country of Origin"
          value={filters.originCountry}
          handleChangeValue={handleChangeValue("originCountry")}
          options={[
            {
              label: "Bangladesh",
              value: "bd",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/bd.svg",
            },
            {
              label: "Czech Republic",
              value: "cz",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/cz.svg",
            },
            {
              label: "China",
              value: "cn",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/cn.svg",
            },
            {
              label: "Japan",
              value: "jp",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/jp.svg",
            },
          ]}
        />
        <SelectBox
          label="Country of Export"
          placeholder="Select Country of Export"
          value={filters.exportCountry}
          handleChangeValue={handleChangeValue("exportCountry")}
          options={[
            {
              label: "Bangladesh",
              value: "bd",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/bd.svg",
            },
            {
              label: "Czech Republic",
              value: "cz",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/cz.svg",
            },
            {
              label: "China",
              value: "cn",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/cn.svg",
            },
            {
              label: "Japan",
              value: "jp",
              imgUrl: "https://hatscripts.github.io/circle-flags/flags/jp.svg",
            },
          ]}
        />
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        p={2}
        sx={{
          background: "#F7F8F8",
        }}
      >
        <Button variant="white-outlined" sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ flex: 1, gap: 1 }}
          onClick={applyFilters}
          disabled={areFilterApplying}
          startIcon={areFilterApplying ? <CircularProgress size={20} /> : null}
        >
          {areFilterApplying ? "Applying" : "Apply"}
        </Button>
      </Stack>
    </DialogMenu>
  );
};

export default AdvanceFilter;
